import { useEffect, useRef } from 'react';

const PING_INTERVAL_MS = 30000;

export default function useReconnectingSocket(url, onMessage) {
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectDelayRef = useRef(1000);
  const shouldReconnectRef = useRef(true);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!url) return undefined;
    shouldReconnectRef.current = true;

    function connect() {
      const socket = new WebSocket(url);
      wsRef.current = socket;

      socket.addEventListener('open', () => {
        reconnectDelayRef.current = 1000;
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'ping' }));
        }, PING_INTERVAL_MS);
      });

      socket.addEventListener('message', (event) => {
        let parsed;
        try {
          parsed = JSON.parse(event.data);
        } catch {
          return;
        }
        onMessageRef.current?.(parsed);
      });

      socket.addEventListener('close', () => {
        wsRef.current = null;
        clearInterval(pingIntervalRef.current);
        if (!shouldReconnectRef.current) return;
        reconnectTimeoutRef.current = setTimeout(connect, reconnectDelayRef.current);
        reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30000);
      });
    }

    connect();

    return () => {
      shouldReconnectRef.current = false;
      clearInterval(pingIntervalRef.current);
      clearTimeout(reconnectTimeoutRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [url]);
}
